/**
 * custom-forever-form.js
 * Handles upload zones, Cloudinary uploads, form validation, and FAQ accordion
 * for the Custom Forever Form product page.
 *
 * Loaded with `defer`. Wrapped in an IIFE to avoid polluting the global scope.
 * All configuration is read from data-* attributes on the section element so
 * no values are hardcoded here.
 */
(function () {
  'use strict';

  /* ================================================================
     CONSTANTS — error/warning copy
     ================================================================ */
  var MESSAGES = {
    typeError:    'Please upload a JPG, PNG, HEIC, or WEBP image.',
    sizeError:    'This image is over {max}MB. Please choose a smaller file or compress it before uploading.',
    dimWarn:      'This image is smaller than we recommend. Higher resolution photos produce a better result. You can continue, but we may contact you for a replacement.',
    uploadFail:   'We couldn\'t upload that image. Please check your connection and try again.',
    missingField: 'Please complete all fields before adding to cart.'
  };

  /* Accepted MIME types (HEIC has two variants) */
  var ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heic-sequence',
    'image/webp'
  ];

  var MIN_DIMENSION = 800; /* px — warn below this threshold */

  /* ================================================================
     UTILITY HELPERS
     ================================================================ */

  /**
   * Shorthand querySelector scoped to an optional root element.
   * @param {string} selector
   * @param {Element} [root]
   * @returns {Element|null}
   */
  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  /**
   * Shorthand querySelectorAll returning an Array.
   * @param {string} selector
   * @param {Element} [root]
   * @returns {Element[]}
   */
  function qsa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  /**
   * Show an element by adding a visibility class.
   * @param {Element} el
   * @param {string} cls — the class to add (default: '--visible')
   */
  function show(el, cls) {
    if (el) el.classList.add(cls || '--visible');
  }

  /**
   * Hide an element by removing a visibility class.
   * @param {Element} el
   * @param {string} cls
   */
  function hide(el, cls) {
    if (el) el.classList.remove(cls || '--visible');
  }

  /* ================================================================
     FILE VALIDATION
     ================================================================ */

  /**
   * Synchronous checks: MIME type and file size.
   * @param {File} file
   * @param {number} maxBytes
   * @returns {{ ok: boolean, code: string|null }}
   */
  function validateFileSync(file, maxBytes) {
    if (ALLOWED_TYPES.indexOf(file.type) === -1) {
      return { ok: false, code: 'type' };
    }
    if (file.size > maxBytes) {
      return { ok: false, code: 'size' };
    }
    return { ok: true, code: null };
  }

  /**
   * Async dimension check. Returns { width, height }.
   * HEIC files cannot be decoded by most browsers, so we skip the check and
   * return dummy dimensions that satisfy the minimum. This avoids a false
   * warning for valid HEIC uploads.
   * @param {File} file
   * @returns {Promise<{ width: number, height: number }>}
   */
  function checkDimensions(file) {
    var isHeic = (file.type === 'image/heic' || file.type === 'image/heic-sequence');
    if (isHeic) {
      /* Cannot decode HEIC natively in most browsers — skip dimension check */
      return Promise.resolve({ width: MIN_DIMENSION + 1, height: MIN_DIMENSION + 1 });
    }

    return new Promise(function (resolve) {
      var objectUrl = URL.createObjectURL(file);
      var img = new Image();

      img.onload = function () {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };

      img.onerror = function () {
        /* If decoding fails, pass through — upload will surface any real issue */
        URL.revokeObjectURL(objectUrl);
        resolve({ width: MIN_DIMENSION + 1, height: MIN_DIMENSION + 1 });
      };

      img.src = objectUrl;
    });
  }

  /* ================================================================
     CLOUDINARY UPLOAD (XHR — required for upload progress events)
     ================================================================ */

  /**
   * Upload a file to Cloudinary via an unsigned upload preset.
   *
   * @param {File}     file
   * @param {string}   cloudName
   * @param {string}   uploadPreset
   * @param {Function} onProgress  — called with (percentComplete: number)
   * @returns {Promise<{ secure_url: string }>}
   */
  function uploadToCloudinary(file, cloudName, uploadPreset, onProgress) {
    return new Promise(function (resolve, reject) {
      var formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      /* folder is enforced by the upload preset in Cloudinary dashboard */

      var xhr = new XMLHttpRequest();
      var endpoint = 'https://api.cloudinary.com/v1_1/' + cloudName + '/image/upload';

      xhr.open('POST', endpoint);

      /* Report upload progress so the progress bar can update */
      if (xhr.upload) {
        xhr.upload.addEventListener('progress', function (e) {
          if (e.lengthComputable && typeof onProgress === 'function') {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
      }

      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(new Error('Unexpected response from upload service.'));
          }
        } else {
          reject(new Error('Upload returned status ' + xhr.status));
        }
      });

      xhr.addEventListener('error', function () {
        reject(new Error('Network error during upload.'));
      });

      xhr.addEventListener('abort', function () {
        reject(new Error('Upload was cancelled.'));
      });

      xhr.send(formData);
    });
  }

  /* ================================================================
     UPLOAD ZONE CLASS
     Each upload slot (front, side, character) is managed by one instance.
     ================================================================ */

  /**
   * @param {Element} itemEl      - the .ff-upload-item wrapper div
   * @param {Element} zoneEl      - the .ff-upload-zone div (child of itemEl)
   * @param {Element} fileInput   - the hidden <input type="file">
   * @param {Element} hiddenInput - the hidden <input> that stores the Cloudinary URL
   * @param {Object}  config      - { cloudName, uploadPreset, maxBytes }
   * @param {Function} onChange   - called after any state change (for form revalidation)
   */
  function UploadZone(itemEl, zoneEl, fileInput, hiddenInput, config, onChange) {
    this.item       = itemEl;
    this.zone       = zoneEl;
    this.fileInput  = fileInput;
    this.hidden     = hiddenInput;
    this.config     = config;
    this.onChange   = onChange || function () {};

    /* Elements inside the zone div */
    this.preview      = qs('.ff-preview',               zoneEl);
    this.previewImg   = qs('.ff-preview__img',           zoneEl);
    this.previewPhld  = qs('.ff-preview__placeholder',   zoneEl);
    this.btnReplace   = qs('.ff-preview__btn--replace',  zoneEl);
    this.btnRemove    = qs('.ff-preview__btn--remove',   zoneEl);
    this.spinner      = qs('.ff-spinner',                zoneEl);
    this.progressBar  = qs('.ff-progress__bar',          zoneEl);
    this.progressText = qs('.ff-progress__text',         zoneEl);

    /*
     * Error and warning messages live OUTSIDE the zone div (they are siblings
     * within .ff-upload-item) so they must be looked up from the item wrapper,
     * not from zoneEl, otherwise querySelector returns null.
     */
    this.errorMsg = qs('.ff-error-msg', itemEl);
    this.warnMsg  = qs('.ff-warn-msg',  itemEl);

    this._bind();
  }

  UploadZone.prototype._bind = function () {
    var self = this;

    /* Click on zone opens file picker */
    this.zone.addEventListener('click', function (e) {
      /* Don't re-trigger if clicking overlay buttons */
      if (e.target.closest && e.target.closest('.ff-preview__btn')) return;
      if (self.zone.classList.contains('ff-upload-zone--success')) return;
      self.fileInput.click();
    });

    /* Keyboard: Enter or Space opens file picker */
    this.zone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!self.zone.classList.contains('ff-upload-zone--success')) {
          self.fileInput.click();
        }
      }
    });

    /* File selected via picker */
    this.fileInput.addEventListener('change', function () {
      if (self.fileInput.files && self.fileInput.files[0]) {
        self._handleFile(self.fileInput.files[0]);
      }
    });

    /* Drag-and-drop */
    this.zone.addEventListener('dragover', function (e) {
      e.preventDefault();
      self.zone.classList.add('ff-upload-zone--dragging');
    });

    this.zone.addEventListener('dragleave', function (e) {
      /* Only clear if leaving the zone itself (not a child element) */
      if (!self.zone.contains(e.relatedTarget)) {
        self.zone.classList.remove('ff-upload-zone--dragging');
      }
    });

    this.zone.addEventListener('drop', function (e) {
      e.preventDefault();
      self.zone.classList.remove('ff-upload-zone--dragging');
      var files = e.dataTransfer && e.dataTransfer.files;
      if (files && files[0]) {
        self._handleFile(files[0]);
      }
    });

    /* Replace button — reopen file picker */
    if (this.btnReplace) {
      this.btnReplace.addEventListener('click', function (e) {
        e.stopPropagation();
        self.fileInput.value = '';
        self.fileInput.click();
      });
    }

    /* Remove button — clear the slot */
    if (this.btnRemove) {
      this.btnRemove.addEventListener('click', function (e) {
        e.stopPropagation();
        self._clear();
      });
    }
  };

  /**
   * Handle a File object: validate, preview, upload.
   * @param {File} file
   */
  UploadZone.prototype._handleFile = function (file) {
    var self = this;

    /* 1. Synchronous validation (type + size) */
    var syncResult = validateFileSync(file, self.config.maxBytes);
    if (!syncResult.ok) {
      self._setError(self._errorText(syncResult.code));
      return;
    }

    /* 2. Clear previous error/warning */
    self._clearMessages();

    /* 3. Set loading state */
    self._setLoading(true);

    /* 4. Async dimension check, then upload */
    checkDimensions(file).then(function (dims) {
      var belowMin = (dims.width < MIN_DIMENSION || dims.height < MIN_DIMENSION);

      /* Show non-blocking warning for low-res images */
      if (belowMin && self.warnMsg) {
        self.warnMsg.textContent = MESSAGES.dimWarn;
        self.warnMsg.classList.add('ff-warn-msg--visible');
      }

      /* Generate preview immediately from local blob (fast, no network) */
      self._showPreview(file);

      /* Upload to Cloudinary */
      return uploadToCloudinary(
        file,
        self.config.cloudName,
        self.config.uploadPreset,
        function (pct) {
          self._setProgress(pct);
        }
      );
    })
    .then(function (result) {
      /* Store the secure CDN URL in the hidden form input */
      self.hidden.value = result.secure_url;
      self._setLoading(false);
      self.zone.classList.add('ff-upload-zone--success');
      self.onChange();
    })
    .catch(function (err) {
      /* Upload failed — clear preview, show error */
      self._setLoading(false);
      self._setError(MESSAGES.uploadFail);
      /* Reset the preview in case we showed one */
      if (self.previewImg) self.previewImg.src = '';
      if (self.previewImg) self.previewImg.classList.add('ff-preview__img--hidden');
      if (self.previewPhld) self.previewPhld.style.display = '';
      console.error('[ForeverForm] Upload error:', err.message);
    });
  };

  /**
   * Generate an object URL preview. Falls back to a placeholder for HEIC.
   * @param {File} file
   */
  UploadZone.prototype._showPreview = function (file) {
    var isHeic = (file.type === 'image/heic' || file.type === 'image/heic-sequence');

    if (isHeic) {
      /* HEIC cannot be decoded by the browser for display purposes */
      if (this.previewImg) this.previewImg.classList.add('ff-preview__img--hidden');
      if (this.previewPhld) {
        this.previewPhld.style.display = 'flex';
        var nameEl = this.previewPhld.querySelector('.ff-preview__placeholder-name');
        if (nameEl) nameEl.textContent = file.name;
      }
    } else {
      /* Revoke any existing object URL to free memory */
      if (this.previewImg && this.previewImg.src && this.previewImg.src.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewImg.src);
      }
      var objectUrl = URL.createObjectURL(file);
      if (this.previewImg) {
        this.previewImg.src = objectUrl;
        this.previewImg.classList.remove('ff-preview__img--hidden');
      }
      if (this.previewPhld) this.previewPhld.style.display = 'none';
    }
  };

  /**
   * Set or clear loading state and update progress bar.
   * @param {boolean} loading
   */
  UploadZone.prototype._setLoading = function (loading) {
    if (loading) {
      this.zone.classList.add('ff-upload-zone--loading');
      this.zone.classList.remove('ff-upload-zone--error');
      this.zone.classList.remove('ff-upload-zone--success');
    } else {
      this.zone.classList.remove('ff-upload-zone--loading');
    }
  };

  /**
   * Update progress bar width and label.
   * @param {number} pct — 0 to 100
   */
  UploadZone.prototype._setProgress = function (pct) {
    if (this.progressBar) this.progressBar.style.width = pct + '%';
    if (this.progressText) this.progressText.textContent = 'Uploading... ' + pct + '%';
  };

  /**
   * Display an error message and apply error styling.
   * @param {string} msg
   */
  UploadZone.prototype._setError = function (msg) {
    this.zone.classList.add('ff-upload-zone--error');
    this.zone.classList.remove('ff-upload-zone--loading');
    if (this.errorMsg) {
      this.errorMsg.textContent = msg;
      this.errorMsg.classList.add('ff-error-msg--visible');
    }
    /* Announce to screen readers */
    if (this.errorMsg) this.errorMsg.setAttribute('role', 'alert');
    this.onChange();
  };

  /**
   * Clear all error and warning messages.
   */
  UploadZone.prototype._clearMessages = function () {
    this.zone.classList.remove('ff-upload-zone--error');
    if (this.errorMsg) {
      this.errorMsg.textContent = '';
      this.errorMsg.classList.remove('ff-error-msg--visible');
      this.errorMsg.removeAttribute('role');
    }
    if (this.warnMsg) {
      this.warnMsg.textContent = '';
      this.warnMsg.classList.remove('ff-warn-msg--visible');
    }
  };

  /**
   * Return the appropriate error message string for a given error code.
   * @param {string} code — 'type' | 'size'
   * @returns {string}
   */
  UploadZone.prototype._errorText = function (code) {
    if (code === 'type') return MESSAGES.typeError;
    if (code === 'size') {
      var maxMb = Math.round(this.config.maxBytes / 1048576);
      return MESSAGES.sizeError.replace('{max}', maxMb);
    }
    return MESSAGES.uploadFail;
  };

  /**
   * Clear the slot completely, returning it to the empty state.
   */
  UploadZone.prototype._clear = function () {
    this.hidden.value = '';
    this.fileInput.value = '';

    /* Remove state classes */
    this.zone.classList.remove('ff-upload-zone--success');
    this.zone.classList.remove('ff-upload-zone--error');
    this.zone.classList.remove('ff-upload-zone--loading');

    /* Revoke and clear preview */
    if (this.previewImg) {
      if (this.previewImg.src && this.previewImg.src.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewImg.src);
      }
      this.previewImg.src = '';
      this.previewImg.classList.remove('ff-preview__img--hidden');
    }
    if (this.previewPhld) this.previewPhld.style.display = '';

    /* Reset progress bar */
    if (this.progressBar) this.progressBar.style.width = '0%';
    if (this.progressText) this.progressText.textContent = '';

    this._clearMessages();
    this.onChange();
  };

  /**
   * Returns true if this slot has a successful upload URL stored.
   * @returns {boolean}
   */
  UploadZone.prototype.hasValue = function () {
    return !!this.hidden.value;
  };

  /* ================================================================
     FORM VALIDATOR
     Reactive: checks validity on every field change, enables/disables
     the Add to Cart button accordingly.
     ================================================================ */

  /**
   * @param {Element}      formEl       — the <form> element
   * @param {UploadZone[]} uploadZones  — array of UploadZone instances
   */
  function FormValidator(formEl, uploadZones) {
    this.form        = formEl;
    this.uploadZones = uploadZones;
    this.submitBtn   = qs('#ff-add-to-cart', formEl);
    this.summary     = qs('.ff-validation-summary', formEl);
    this.variantInput = qs('#ff-variant-id', formEl);
    this.checkbox     = qs('#ff-acknowledge', formEl);

    this._bind();
    this.check(); /* initial state */
  }

  /**
   * Returns true only when all required fields are satisfied.
   * @returns {boolean}
   */
  FormValidator.prototype.isValid = function () {
    var variantOk  = !!(this.variantInput && this.variantInput.value);
    var checkOk    = !!(this.checkbox && this.checkbox.checked);
    var uploadsOk  = this.uploadZones.every(function (z) { return z.hasValue(); });

    /* Dog breed field must be filled */
    var breedInput = document.getElementById('ff-dog-breed');
    var breedOk = !!(breedInput && breedInput.value.trim());

    /* If "With Name Plate" is selected, the nameplate text must be filled */
    var nameplateInput = document.getElementById('ff-nameplate-text');
    var nameplateField = document.getElementById('ff-nameplate-field');
    var nameplateOk = true;
    if (nameplateInput && nameplateField && !nameplateField.hidden) {
      nameplateOk = !!nameplateInput.value.trim();
    }

    return variantOk && checkOk && uploadsOk && breedOk && nameplateOk;
  };

  /**
   * Enable or disable the CTA based on current validity.
   */
  FormValidator.prototype.check = function () {
    var valid = this.isValid();
    if (this.submitBtn) {
      this.submitBtn.disabled = !valid;
    }
  };

  /**
   * Bind change listeners to all relevant inputs.
   */
  FormValidator.prototype._bind = function () {
    var self = this;
    var recheck = function () { self.check(); };

   /* Option tiles: listen for variant-change event dispatched by initVariantTiles */
    this.form.addEventListener('ff:variant-change', recheck);

    if (this.checkbox) this.checkbox.addEventListener('change', recheck);

    /* Breed field — recheck on input */
    var breedInput = qs('#ff-dog-breed', this.form);
    if (breedInput) breedInput.addEventListener('input', recheck);

    /* Nameplate text field — recheck on input */
    var nameplateInput = qs('#ff-nameplate-text', this.form);
    if (nameplateInput) nameplateInput.addEventListener('input', recheck);
  };

  /**
   * Show an inline validation summary message and scroll it into view.
   * Called on submit attempt when form is invalid.
   */
  FormValidator.prototype.showSummary = function () {
    if (!this.summary) return;
    this.summary.textContent = MESSAGES.missingField;
    this.summary.classList.add('ff-validation-summary--visible');
    this.summary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  FormValidator.prototype.hideSummary = function () {
    if (this.summary) this.summary.classList.remove('ff-validation-summary--visible');
  };

  /* ================================================================
     FAQ ACCORDION
     ================================================================ */

  /**
   * Initialise the FAQ accordion within a given root element.
   * Uses the CSS grid-template-rows trick for smooth height animation.
   * @param {Element} root
   */
  function initFAQ(root) {
    var items = qsa('.ff-faq__item', root);

    items.forEach(function (item) {
      var btn    = qs('.ff-faq__question', item);
      var answer = qs('.ff-faq__answer',   item);
      if (!btn || !answer) return;

      /* Set initial ARIA state */
      btn.setAttribute('aria-expanded', 'false');
      answer.setAttribute('role', 'region');

      btn.addEventListener('click', function () {
        var isOpen = item.classList.contains('ff-faq__item--open');

        /* Close all other items */
        items.forEach(function (other) {
          if (other !== item) {
            other.classList.remove('ff-faq__item--open');
            var otherBtn = qs('.ff-faq__question', other);
            if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
          }
        });

        /* Toggle this item */
        if (isOpen) {
          item.classList.remove('ff-faq__item--open');
          btn.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('ff-faq__item--open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ================================================================
     VARIANT TILE INITIALISATION
     Reads the first checked radio (if any) to seed the hidden variant ID.
     ================================================================ */

  /**
   * Resolve current option selections to a variant ID, update hidden input,
   * and update the displayed price.
   */
  function initVariantTiles(formEl) {
    var hiddenVariant = qs('#ff-variant-id', formEl);
    var priceEl = qs('#ff-current-price', formEl);
    var dataEl = qs('#ff-variants-data', formEl);
    if (!hiddenVariant || !dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[ForeverForm] Could not parse variants data:', e);
      return;
    }

    /**
     * Read the currently selected option values across all option groups.
     */
    function getSelectedOptions() {
      var optionBlocks = qsa('.ff-option-block', formEl);
      return optionBlocks.map(function (block) {
        var checkedRadio = qs('.ff-option-tile__input:checked', block);
        return checkedRadio ? checkedRadio.value : null;
      });
    }

    /**
     * Find the variant matching the current option selections.
     */
    function resolveVariant() {
      var selected = getSelectedOptions();
      if (selected.indexOf(null) !== -1) return null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < v.options.length; j++) {
          if (v.options[j] !== selected[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    /**
     * Update hidden variant ID, price display, and dispatch event for validator.
     */
    function updateState() {
      var variant = resolveVariant();
      if (variant) {
        hiddenVariant.value = variant.id;
        if (priceEl) priceEl.textContent = variant.price_formatted;
      } else {
        hiddenVariant.value = '';
      }
      var event = new CustomEvent('ff:variant-change', { detail: { variant: variant } });
      formEl.dispatchEvent(event);
    }

    /* Bind change events on every option radio */
    qsa('.ff-option-tile__input', formEl).forEach(function (radio) {
      radio.addEventListener('change', updateState);
    });

    /* Set initial state */
    updateState();
  }

  /* ================================================================
     FORM SUBMIT GUARD
     ================================================================ */

  /**
   * Attach a submit listener that blocks submission if validation fails.
   * @param {Element}       formEl
   * @param {FormValidator} validator
   */
  function initSubmitGuard(formEl, validator) {
    formEl.addEventListener('submit', function (e) {
      if (!validator.isValid()) {
        e.preventDefault();
        validator.showSummary();
      } else {
        validator.hideSummary();
      }
    });
  }
/* ================================================================
     NAMEPLATE FIELD — conditional reveal
     Shows the nameplate text input only when "With Name Plate" is selected.
     ================================================================ */

  /**
   * Watch for option changes and toggle visibility of the nameplate text field.
   * Triggered by the ff:variant-change event dispatched by initVariantTiles.
   * @param {Element} formEl
   */
  function initNameplateField(formEl) {
    var field = qs('#ff-nameplate-field', formEl);
    var input = qs('#ff-nameplate-text', formEl);
    if (!field || !input) return;

    function updateVisibility() {
      /* Look at the selected Name Plate radio specifically */
      var selectedRadios = qsa('.ff-option-tile__input:checked', formEl);
      var withNamePlate = selectedRadios.some(function (radio) {
        var value = (radio.value || '').toLowerCase();
        return value.indexOf('with name plate') !== -1;
      });

      if (withNamePlate) {
        field.hidden = false;
        input.required = true;
      } else {
        field.hidden = true;
        input.required = false;
        input.value = '';
      }
    }

    /* Re-check whenever any option changes */
    formEl.addEventListener('ff:variant-change', updateVisibility);

    /* Initial state */
    updateVisibility();
  }
  /* ================================================================
     INITIALISATION
     ================================================================ */

  function init() {
    /* Find the section element that carries config data attributes */
    var section = qs('[data-ff-section]');
    if (!section) return; /* Not on this page — exit cleanly */

    var cloudName    = section.getAttribute('data-cloud-name')    || 'fetchandform';
    var uploadPreset = section.getAttribute('data-upload-preset') || 'fetch_form_forever';
    var maxBytes     = parseInt(section.getAttribute('data-max-size'), 10) || 10485760;

    var config = { cloudName: cloudName, uploadPreset: uploadPreset, maxBytes: maxBytes };

    /* Form element */
    var formEl = qs('#ff-product-form');
    if (!formEl) return;

    /* Initialise variant tiles */
    initVariantTiles(formEl);

    /* Initialise upload zones */
    var uploadZones = [];
    qsa('.ff-upload-item[data-slot]', formEl).forEach(function (item) {
      var slot       = item.getAttribute('data-slot');
      var zoneEl     = qs('.ff-upload-zone',   item);
      var fileInput  = qs('.ff-file-input',     item);
      var hiddenInput = qs('.ff-url-hidden',   item);

      if (!zoneEl || !fileInput || !hiddenInput) return;

      /*
       * Pass `item` as the first argument so the zone can find error/warning
       * message elements that are siblings of zoneEl (not children of it).
       * The onChange closure uses `validator` by reference; it will be defined
       * by the time onChange is ever called (after user interaction).
       */
      var zone = new UploadZone(item, zoneEl, fileInput, hiddenInput, config, function () {
        if (validator) validator.check();
      });

      uploadZones.push(zone);
    });

    /* Initialise form validator */
    var validator = new FormValidator(formEl, uploadZones);

    /* Submit guard */
    initSubmitGuard(formEl, validator);

    /* Nameplate field — conditional show/hide based on Name Plate option */
    initNameplateField(formEl);

    /* FAQ accordion — may be in a separate section, use document scope */
    initFAQ(document);
  }

  /* Run after DOM is ready. `defer` guarantees the script runs after parsing,
     but we guard with DOMContentLoaded for safety when the script is inlined. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();